// @flow
import * as React from 'react';
import cn from 'classnames';
import * as Yup from 'yup';
// eslint-disable-next-line import/named
import { FormattedMessage, type IntlShape } from 'react-intl';

// Components
import { Formik, Form, FormikActions } from 'formik';
import { TextField, FormikField } from 'components/api/';
import { Button } from 'components/atoms';

import './ForgotUsername.scss';

// Types
import { gtmUserEventType } from 'types/gtm';

// Utils
import { isObjEmpty } from 'utils/varValidation';

type Props = {
  className?: string,
  forgotUserName: (email: string) => Object,
  close: () => void,
  gtmUserEvent: gtmUserEventType,
  intl: IntlShape
};

type Values = {
  email: string
};

const validationSchema = () => {
  return Yup.object().shape({
    email: Yup.string()
      .required()
      .email()
  });
};

export default class ForgotUsername extends React.Component<Props> {
  static block = 'forgot-username';
  constructor(props: Props) {
    super(props);
    (this: any).onSubmit = this.onSubmit.bind(this);
    (this: any).formikForm = React.createRef();
    (this: any).emailInput = React.createRef();
  }

  initialValues = {
    email: ''
  };
  formikForm: React.Ref<any>;
  emailInput: React.Ref<'input'>;

  async onSubmit(values: Values, actions: FormikActions<Values>) {
    (this: any).emailInput.current.blur();
    const { email } = values;
    const { setSubmitting, setStatus, resetForm } = actions;
    const { forgotUserName, close, gtmUserEvent } = this.props;
    const data = await forgotUserName(email);
    const success = data && data.success;

    setSubmitting(false);
    setStatus({ success });
    if (success) {
      resetForm(this.initialValues);
      close();
    }
    gtmUserEvent(
      'Send Button',
      'Clicked',
      success
        ? 'Email successfully sent!'
        : "Sorry we couldn't find an account with this email",
      success ? 'Forgot Username Modal > Sign In Page' : 'Forgot Username Modal'
    );
  }

  resetForm() {
    const formikForm = (this: any).formikForm.current;
    formikForm.handleReset();
  }

  render() {
    const me = this;
    return (
      <Formik
        initialValues={this.initialValues}
        validationSchema={validationSchema()}
        onSubmit={this.onSubmit}
        ref={this.formikForm}
        render={({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          dirty,
          status
        }) => {
          const { className, intl } = this.props;
          const ns = ForgotUsername.block;
          const classProps = cn(ns, className);
          const disabled = !dirty || !isObjEmpty(errors);
          const errorClassProps = cn(`${ns}__error-msg text-center`, {
            visible: status && !status.success,
            invisible: !status || status.success
          });
          return (
            <Form className={classProps} onSubmit={handleSubmit}>
              <FormattedMessage id="c8.common.forgot.username">
                {text => (
                  <div className={`${ns}__title text-center`}>{text}</div>
                )}
              </FormattedMessage>

              <FormattedMessage id="c8.forgot.username.we.ll.email.your.username.and.instructions.to.reset.your.password">
                {text => (
                  <div className={`${ns}__subtitle text-center`}>{text}</div>
                )}
              </FormattedMessage>

              <div className={`${ns}__form-fields`}>
                <FormattedMessage id="c8.common.email">
                  {text => (
                    <FormikField
                      renderedComponent={TextField}
                      name="email"
                      type="email"
                      placeholder={text}
                      htmlRef={me.emailInput}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.forgot.username.email.input',
                        defaultMessage: 'Enter email to recover your username'
                      })}
                    />
                  )}
                </FormattedMessage>
              </div>

              <FormattedMessage id="c8.forgot.username.sorry.we.couldn.t.find.an.account.with.this.email">
                {text => <div className={errorClassProps}>{text}</div>}
              </FormattedMessage>

              <Button
                type="Submit"
                size="medium"
                disabled={disabled}
                className={`${ns}__submit-btn d-flex`}
                tabIndex={0}
              >
                {disabled ? (
                  <FormattedMessage id="c8.common.enter.email">
                    {msg => msg}
                  </FormattedMessage>
                ) : (
                  <FormattedMessage id="c8.forgot.username.send">
                    {msg => msg}
                  </FormattedMessage>
                )}
                {isSubmitting && <div className="ld ld-ring ld-spin-fast" />}
              </Button>
            </Form>
          );
        }}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/pages/User/SignIn/components/ForgotUsername/ForgotUsername.js